import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { groupBy, size } from 'lodash';
import { VictoryChart, VictoryAxis, VictoryBar, Line, VictoryLabel } from 'victory';

import { STATUS_COLOR_MAP, getDomainForDay, getBeginDate, getEndDate } from './Timeline.helpers';

import { timelineChartAxisX } from './TimelineChart.parts';
import { chartTheme, chartLabelStyles } from '../../common/ChartTheme';
import { ChartTooltip } from './ChartTooltip';
import { TimelineInfoTooltipContent } from '../Tooltip/TimelineInfoTooltipContent';
import { RoundedBottomBorder, RoundedBottomBorderNoData } from '../_charts/Chart.styles';
import { TurbineStatusChartRow } from '../TurbineStatusChart/TurbineStatusChartRow';
import { TimelineLabel } from './TimelineLabel';
import { barCornerRadius } from '../_charts/Chart.utils';
import { defaultVictoryContainer } from '../_charts/Chart.defaults';

export const insertHeaderRows = (groupedData) => {
    let reorderedTimelineData = [];

    if (groupedData['TURBINE STATUS']) {
        reorderedTimelineData.push({
            key: 'TURBINE_STATUS_HEADER',
        });
        reorderedTimelineData = reorderedTimelineData.concat(groupedData['TURBINE STATUS']);
    }
    if (groupedData.NOTES) {
        reorderedTimelineData.push({
            key: 'NOTES_HEADER',
            type: 'NOTES',
            count: groupedData.NOTES.length,
        });
        reorderedTimelineData = reorderedTimelineData.concat(groupedData.NOTES);
    }
    if (groupedData.EXCEPTIONS) {
        reorderedTimelineData.push({
            key: 'EXCEPTIONS_HEADER',
            type: 'EXCEPTIONS',
            count: groupedData.EXCEPTIONS.length,
        });
        reorderedTimelineData = reorderedTimelineData.concat(groupedData.EXCEPTIONS);
    }
    if (groupedData.RESTRICTIONS) {
        reorderedTimelineData.push({
            key: 'RESTRICTIONS_HEADER',
            type: 'RESTRICTIONS',
            count: groupedData.RESTRICTIONS.length,
        });
        reorderedTimelineData = reorderedTimelineData.concat(groupedData.RESTRICTIONS);
    }
    reorderedTimelineData = JSON.parse(JSON.stringify(reorderedTimelineData));
    return reorderedTimelineData.map((v, i) => {
        // props.text in TimelineLabel expects ordered ints as strings starting from 1
        v.id = (i + 1).toString();
        return v;
    });
};

export const TimelineCollapsibleChart = ({ timelineData, chartWidth, itemLabelWidth }) => {
    const headerWidth = 500;
    const rowHeight = 35;
    const itemHeight = 15;
    const timeAxisHeight = 35;
    const bordersFix = 2;

    const chartPadding = {
        left: itemLabelWidth - bordersFix / 2,
        right: -bordersFix,
        top: timeAxisHeight,
        bottom: timeAxisHeight,
    };

    if (!timelineData) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} />;
    }

    if (timelineData.length === 0) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} message="No data for selected period" />;
    }

    const internalChartDay = timelineData[0] ? moment(timelineData[0].beginDate).toDate() : moment().toDate();

    const groupedData = groupBy(timelineData, 'key');
    const reorderedTimelineData = insertHeaderRows(groupedData);

    // To keep row height consistenly at (rowHeight), we have to calculate chart height
    const timelineRows = size(reorderedTimelineData);
    const calculatedChartHeight = rowHeight * timelineRows + chartPadding.top + chartPadding.bottom;

    const tickLabelComponent = (
        <TimelineLabel data={reorderedTimelineData} width={headerWidth} rowComponent={<TurbineStatusChartRow />} />
    );

    return (
        <RoundedBottomBorder>
            <VictoryChart
                theme={chartTheme}
                domainPadding={{ x: [-rowHeight / 2, rowHeight / 2] }}
                height={calculatedChartHeight}
                width={chartWidth - bordersFix}
                padding={chartPadding}
                domain={{
                    y: getDomainForDay(internalChartDay),
                    x: [0, timelineRows],
                }}
                containerComponent={defaultVictoryContainer}>
                <VictoryAxis
                    key="1"
                    invertAxis
                    name="entity-axis"
                    orientation="left"
                    tickLabelComponent={tickLabelComponent || <VictoryLabel dx={-50} />}
                    gridComponent={<Line x1={0} />}
                    fixLabelOverlap
                    tickCount={timelineRows}
                    style={{
                        tickLabels: chartLabelStyles,
                        grid: {
                            strokeWidth: () => rowHeight + 1,
                            stroke: (datum) => (datum.tickValue % 2 ? '#333D4E' : '#434C5A'),
                        },
                    }}
                />
                {timelineChartAxisX({
                    orientation: 'bottom',
                    timeAxisHeight,
                    chartDay: internalChartDay,
                })}
                {timelineChartAxisX({
                    orientation: 'top',
                    timeAxisHeight,
                    chartDay: internalChartDay,
                })}
                <VictoryBar
                    horizontal
                    key="1"
                    name="single-turbine-chart"
                    cornerRadius={barCornerRadius}
                    labelComponent={<ChartTooltip content={TimelineInfoTooltipContent} />}
                    style={{
                        data: {
                            width: itemHeight,
                            fill: ({ datum }) => STATUS_COLOR_MAP[datum.type],
                            stroke: ({ datum }) => STATUS_COLOR_MAP[datum.type],
                            strokeWidth: 1,
                            fillOpacity: 0.75,
                        },
                    }}
                    x={(d) => d.id}
                    y={getBeginDate}
                    y0={getEndDate}
                    data={reorderedTimelineData}
                />
            </VictoryChart>
        </RoundedBottomBorder>
    );
};

TimelineCollapsibleChart.displayName = 'TimelineCollapsibleChart';

TimelineCollapsibleChart.propTypes = {
    timelineData: PropTypes.array,
    chartWidth: PropTypes.number,
    itemLabelWidth: PropTypes.number,
};

TimelineCollapsibleChart.defaultProps = {
    chartWidth: 1890,
    itemLabelWidth: 400,
};
